import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  IconPicker,
  Input,
  Label,
  Separator,
  Textarea,
} from '@zond/ui';
import { Loader2, LucideIcon, Save } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUpdateProject } from '../../projects.queries';
import { ProjectIntegrationsList } from './project-integrations-list';
import { ProjectMembersList } from './project-members-list';

const availableIcons = Object.keys(LucideIcons).filter(
  (name) => name !== 'createLucideIcon' && name !== 'default'
);

interface ProjectDetailContentProps {
  project: any;
}

export function ProjectDetailContent({ project }: ProjectDetailContentProps) {
  const { t } = useTranslation('projects');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string | undefined>(undefined);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [integrationMappings, setIntegrationMappings] = useState<
    Array<{ integrationType: string; externalId: string; metadata?: any }>
  >([]);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setIcon(project.icon || undefined);
      const userIds = project.UserProject?.map((up: any) => up.user.id) || [];
      setSelectedUserIds(userIds);

      const mappings: Array<{ integrationType: string; externalId: string; metadata?: any }> = [];
      if (project.IntegrationProjectMapping) {
        project.IntegrationProjectMapping.forEach((mapping: any) => {
          mappings.push({
            integrationType: mapping.workspaceIntegration.integrationType,
            externalId: mapping.externalId,
            metadata: mapping.metadata,
          });
        });
      }
      setIntegrationMappings(mappings);
    }
  }, [project]);

  const updateProject = useUpdateProject(
    () => {
      toast.success(t('form.toast.updated'));
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    () => {
      toast.error(t('form.toast.updateError'));
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProject.mutate({
      projectId: project.id,
      name,
      description,
      icon: icon || '',
      userIds: selectedUserIds,
      integrationMappings,
    });
  };

  const hasChanges = () => {
    const nameChanged = name !== project.name;
    const descChanged = description !== (project.description || '');
    const iconChanged = icon !== project.icon;

    const currentUserIds = project.UserProject?.map((up: any) => up.user.id).sort() || [];
    const newUserIds = [...selectedUserIds].sort();
    const usersChanged = JSON.stringify(currentUserIds) !== JSON.stringify(newUserIds);

    const currentMappings =
      project.IntegrationProjectMapping?.map((m: any) => ({
        integrationType: m.workspaceIntegration.integrationType,
        externalId: m.externalId,
        metadata: m.metadata,
      })).sort((a: any, b: any) =>
        `${a.integrationType}-${a.externalId}`.localeCompare(`${b.integrationType}-${b.externalId}`)
      ) || [];

    const newMappings = [...integrationMappings].sort((a, b) =>
      `${a.integrationType}-${a.externalId}`.localeCompare(`${b.integrationType}-${b.externalId}`)
    );

    const mappingsChanged = JSON.stringify(currentMappings) !== JSON.stringify(newMappings);

    return nameChanged || descChanged || iconChanged || usersChanged || mappingsChanged;
  };

  const IconComponent = icon ? (LucideIcons[icon as keyof typeof LucideIcons] as LucideIcon) : null;

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-muted">
              {IconComponent && icon ? (
                <IconComponent className="h-6 w-6 text-muted-foreground" />
              ) : (
                <span className="text-2xl">📁</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate">{name || t('detail.untitled')}</h1>
              <p className="text-muted-foreground truncate">{description || t('detail.noDescription')}</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={!hasChanges() || updateProject.isPending}>
            {updateProject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {t('common:actions.saveChanges')}
          </Button>
        </div>
      </div>
      <Separator className="shadow" />

      <div className="flex-1 min-h-0 overflow-auto p-6">
        <div className="mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('detail.infoTitle')}</CardTitle>
                  <CardDescription>{t('detail.infoDescription')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('form.nameLabel')}</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('form.namePlaceholder')}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="icon">{t('form.iconLabel')}</Label>
                      <IconPicker value={icon} onChange={setIcon} placeholder={t('form.iconPlaceholder')} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">{t('common:labels.description')}</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t('form.descriptionPlaceholder')}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              <ProjectMembersList
                projectId={project.id}
                selectedUserIds={selectedUserIds}
                onUserIdsChange={setSelectedUserIds}
              />
            </div>

            {/* Right Column - Integrations */}
            <div className="space-y-6">
              <ProjectIntegrationsList
                projectId={project.id}
                integrationMappings={integrationMappings}
                onIntegrationMappingsChange={setIntegrationMappings}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
